import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TeamSummary {
  id: string;
  name: string;
  slug: string;
  is_personal: boolean;
}

// `teams` lives in the default Supabase project but isn't in the generated
// Database types here, so cast through `any` for these two queries.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb: any = supabase;

// Batch-fetch a set of team rows by id. Used to resolve owner_id (a text uuid
// stored on vault tables like pulse_digest_configs) into a human team name.
export function useTeams(ids: string[]) {
  const sorted = [...new Set(ids)].sort();
  return useQuery({
    queryKey: ['teams', sorted],
    queryFn: async (): Promise<Map<string, TeamSummary>> => {
      if (sorted.length === 0) return new Map();
      const { data, error } = await sb
        .from('teams')
        .select('id, name, slug, is_personal')
        .in('id', sorted);
      if (error) throw error;
      const map = new Map<string, TeamSummary>();
      for (const t of (data ?? []) as TeamSummary[]) map.set(t.id, t);
      return map;
    },
    enabled: sorted.length > 0,
    staleTime: 5 * 60_000,
  });
}

export function useTeam(id: string | undefined) {
  return useQuery({
    queryKey: ['team', id],
    queryFn: async (): Promise<TeamSummary | null> => {
      if (!id) return null;
      const { data, error } = await sb
        .from('teams')
        .select('id, name, slug, is_personal')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as TeamSummary | null) ?? null;
    },
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}
